import { UserManager } from "oidc-client";
import { ArticleDto } from "../dtos/articleDto";
import httpClient from "./httpClient";
import FileSaver from "file-saver";
import * as signalR from "@microsoft/signalr";

export default {
	async bootstrap(context: any) {
		return new Promise<void>((resolve, reject) => {
			console.log("start bootstrap application");
			// Einmaliger Storage-Wipe beim Umstieg auf den Authorization Code Flow.
			// Alte Implicit-Flow-Sessions haben keinen Refresh-Token; ihr automatischer
			// Silent-Renew läuft über den iframe/authorize-Weg und scheitert am jetzt
			// code-only Identityserver (unsupported_response_type). Darum beim ersten
			// Laden dieser Version alle oidc-Einträge einmalig entfernen, sodass jeder
			// Client sauber neu im Code Flow anmeldet (und einen Refresh-Token bekommt).
			const AUTH_MIGRATION = "codeflow-v1";
			if (window.localStorage.getItem("authMigration") !== AUTH_MIGRATION) {
				Object.keys(window.localStorage)
					.filter((k) => k.startsWith("oidc."))
					.forEach((k) => window.localStorage.removeItem(k));
				window.localStorage.setItem("authMigration", AUTH_MIGRATION);
			}
			var client = new UserManager(context.state.oidc.config);
			// Add event when token expires. This is just for monitoring
			// at the beginning. Can be removed later.
			client.events.addAccessTokenExpiring(function () {
				console.log("helpdesk token expiring...");
			});
			context.commit("storeOidcInstance", client);
			console.log("checking user authentication");
			context.state.oidc.instance
				.getUser()
				.then((user: any) => {
					if (user === null || user.expired) {
						console.log("user is not authenticated - redirect to signin");
						// Store the actually requested url so we can redirect
						// after authentication to this page.
						window.sessionStorage.setItem("returnUrl", window.location.href);
						context.state.oidc.instance.signinRedirect();
					} else {
						console.log("user authenticated");
						context.commit("storeOidcUser", user);
						resolve();
					}
				})
				.catch((err: any) => {
					// Ein alter/inkompatibler gespeicherter Token (z.B. von vor einem
					// Identityserver-Wechsel) lässt getUser() fehlschlagen. Ohne diesen
					// catch bleibt die Seite dauerhaft weiss, weil das Promise nie
					// aufgelöst wird. Gespeicherten Token löschen und neu anmelden.
					console.log(
						"stored token is invalid, clearing it and redirecting to signin",
						err
					);
					context.state.oidc.instance.removeUser().finally(() => {
						window.sessionStorage.setItem("returnUrl", window.location.href);
						context.state.oidc.instance.signinRedirect();
					});
				});
		});
	},
	async checkAccess(context: any) {
		console.log("check access");
		const response = await httpClient().get(`User/GetIsAdmin`);
		context.commit("setIsAdmin", response.data);
	},
	async loadManufacturers(context: any) {
		try {
			const response = await httpClient().get(`Manufacturer/GetManufacturers`);
			context.commit("setManufacturers", response.data);
		} catch {
			// vermutlich keine Berechtigung, aber das ist nicht schlimm
		}
	},
	logout(context: any) {
		console.log("redirecting user to signout");
		window.localStorage.removeItem("access_token");
		context.state.oidc.instance.signoutRedirect();
	},
	async updateArticle(context: any, articleId: number) {
		const articleInStore = context.state.articles.find(
			(a: ArticleDto) => a.articleId == articleId
		);
		if (articleInStore != undefined) {
			// nur wenn der Artikel lokal vorhanden ist, wird er erneuert.
			var response = await httpClient().get(`article/getArticleById?articleId=${articleId}`);
			if (response.data != null && response.data != undefined) {
				context.commit("replaceArticle", response.data);
				if (response.data.calculating) {
					context.dispatch("updateValidationProgress", [response.data.articleId]);
				} else {
					const data = { articleId: response.data.articleId, stepProgresses: [] };
					context.commit("setValidationProgress", data);
				}
			}
		}
	},

	async updateValidationProgress(context: any, articleIds: number[]) {
		var response = await httpClient().post(`article/GetValidationProgresses`, articleIds);
		response.data.forEach((articleProgress: any) => {
			context.commit("setValidationProgress", articleProgress);
		});
	},

	/**
	 * Loads all sub articles that are not in the state yet.
	 * This is used by the assembly component to load the subarticles.
	 * @param context
	 * @param articleIds Ids of articles to load.
	 * @returns
	 */
	async loadMissingSubArticles(context: any, articleIds: number[]) {
		// Find missing articles
		articleIds = articleIds.filter(
			(a) => !context.state.articles.some((sa: ArticleDto) => sa.articleId == a)
		);
		if (articleIds.length <= 0) {
			return;
		}

		try {
			context.state.loading = true;
			let response = await httpClient().post("article/GetArticlesByIds", articleIds);
			context.commit("addOrUpdateArticles", response.data);
		} catch (err) {
			console.error(`Failed to load missing articles. Error: ${err}`);
		} finally {
			context.state.loading = false;
		}
	},

	async downloadOriginalImportFile(context: any, articleId: number) {
		try {
			const response = await httpClient().get(
				`article/GetOriginalImportFile?articleId=${articleId}`,
				{
					method: "GET",
					responseType: "blob",
				}
			);
			var filename = response.headers["content-disposition"].split('filename="')[1];
			if (filename == undefined) {
				filename = response.headers["content-disposition"].split("filename=")[1];
				filename = filename.split("; filename*=")[0];
			} else {
				filename = filename.split('"; filename*=')[0];
			}
			FileSaver.saveAs(new Blob([response.data]), filename);
		} catch (err) {
			console.error(
				`Die Originaldatei des Artikels konnte nicht geladen werden. Error: ${err}`
			);
		}
	},

	async downloadInvoiceAsPdf(context: any, invoiceId: number) {
		console.log(`Downloading invoice with id ${invoiceId}`);
		const response = await httpClient().get(`CustomerOrder/GetInvoice?invoiceId=${invoiceId}`, {
			responseType: "blob",
		});
		const url = window.URL.createObjectURL(new Blob([response.data]));
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", `Rechnung ${invoiceId}.pdf`);
		link.click();
	},

	setupSignalR(context: any) {
		// @ts-ignore
		const connection = new signalR.HubConnectionBuilder()
			.withUrl(process.env.VUE_APP_eventUrl + "signalhub", {
				accessTokenFactory: () => {
					//console.log('signalR reads token');
					const userString = window.localStorage.getItem(
						`oidc.user:${process.env.VUE_APP_authorityUrl}:HelpdeskClient`
					);
					// @ts-ignore
					if (userString != null) {
						const user = JSON.parse(userString);
						return user.access_token;
					} else {
						console.log("Kein Token für SignalR vorhanden.");
						return null;
					}
				},
			})
			.configureLogging(signalR.LogLevel.Information)
			.build();

		async function start() {
			try {
				await connection.start();
				console.log("SignalR connected");
			} catch (err) {
				console.log(err);
				setTimeout(() => start(), 5000);
			}
		}

		connection.onclose(async () => {
			console.log("signalR closed");
			await start();
		});

		connection.on("ArticleUpdatedAdministrators", (message: any) => {
			const article = JSON.parse(message.jsonObject);
			context.dispatch("updateArticle", article.articleId);
			console.log(`Article ${article.articleId} was updated`);
		});

		connection.on("ArticleUpdated", (message: any) => {
			// Prevent warning
		});

		connection.on("MessageForArticle", (articleId: number, message: string) => {
			console.log("MessageForArticle received. " + message);
			//context.commit('setCalculatingMessage', {
			//    articleId,
			//    message,
			//});
		});

		// Start the connection.
		start();
	},
};
